/** @file IHF handball field rendering on the field layer. */
import Konva from "konva";
import { metersToPx, metersToPxLength } from "../core/coords.js";

const LINE_COLOR = "#2b2b2b";
const FIELD_FILL = "#ffe9c4";
const GOAL_AREA_FILL = "#fde68a";
const GOAL_COLOR = "#1f2937";

const STROKE_LINE_M = 0.05;
const STROKE_GOAL_M = 0.08;
const DASH_ON_M = 0.4;
const DASH_OFF_M = 0.3;

/**
 * Clear and (re)draw the field on the given layer.
 * @param {import("konva/lib/Layer.js").Layer} layer
 * @param {import("../core/coords.js").FieldTransform} t
 */
export function drawField(layer, t) {
  layer.destroyChildren();
  layer.clearCache();

  const strokePx = Math.max(1, metersToPxLength(t, STROKE_LINE_M));
  const goalStrokePx = Math.max(2, metersToPxLength(t, STROKE_GOAL_M));
  const dashPx = [metersToPxLength(t, DASH_ON_M), metersToPxLength(t, DASH_OFF_M)];

  const topLeft = metersToPx(t, { x: 0, y: 0 });
  const fieldW = metersToPxLength(t, 40);
  const fieldH = metersToPxLength(t, 20);

  layer.add(
    new Konva.Rect({
      x: topLeft.px,
      y: topLeft.py,
      width: fieldW,
      height: fieldH,
      fill: FIELD_FILL,
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      listening: false,
    }),
  );

  const centerTop = metersToPx(t, { x: 20, y: 0 });
  const centerBottom = metersToPx(t, { x: 20, y: 20 });
  layer.add(
    new Konva.Line({
      points: [centerTop.px, centerTop.py, centerBottom.px, centerBottom.py],
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      listening: false,
    }),
  );

  drawGoalArea(layer, t, "left", strokePx);
  drawGoalArea(layer, t, "right", strokePx);
  drawFreeThrowArea(layer, t, "left", strokePx, dashPx);
  drawFreeThrowArea(layer, t, "right", strokePx, dashPx);
  draw7mLine(layer, t, "left", strokePx);
  draw7mLine(layer, t, "right", strokePx);
  draw4mLine(layer, t, "left", strokePx);
  draw4mLine(layer, t, "right", strokePx);
  drawGoal(layer, t, "left", goalStrokePx);
  drawGoal(layer, t, "right", goalStrokePx);

  layer.cache();
  layer.batchDraw();
}

/**
 * @param {import("konva/lib/Layer.js").Layer} layer
 * @param {import("../core/coords.js").FieldTransform} t
 * @param {"left" | "right"} side
 * @param {number} strokePx
 */
function drawGoalArea(layer, t, side, strokePx) {
  const radiusM = 6;
  const goalX = side === "left" ? 0 : 40;
  const innerX = side === "left" ? radiusM : 40 - radiusM;
  const radiusPx = metersToPxLength(t, radiusM);

  // Fill
  layer.add(buildGoalAreaFill(t, side));

  // Upper arc — spans from outside field (behind goal) inward
  const upperCenter = metersToPx(t, { x: goalX, y: 8.5 });
  layer.add(
    new Konva.Arc({
      x: upperCenter.px,
      y: upperCenter.py,
      innerRadius: radiusPx,
      outerRadius: radiusPx,
      angle: 90,
      rotation: side === "left" ? 270 : 180,
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      listening: false,
    }),
  );

  // Straight segment parallel to goal line
  const segTop = metersToPx(t, { x: innerX, y: 8.5 });
  const segBottom = metersToPx(t, { x: innerX, y: 11.5 });
  layer.add(
    new Konva.Line({
      points: [segTop.px, segTop.py, segBottom.px, segBottom.py],
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      listening: false,
    }),
  );

  // Lower arc
  const lowerCenter = metersToPx(t, { x: goalX, y: 11.5 });
  layer.add(
    new Konva.Arc({
      x: lowerCenter.px,
      y: lowerCenter.py,
      innerRadius: radiusPx,
      outerRadius: radiusPx,
      angle: 90,
      rotation: side === "left" ? 0 : 90,
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      listening: false,
    }),
  );
}

/**
 * @param {import("../core/coords.js").FieldTransform} t
 * @param {"left" | "right"} side
 * @returns {Konva.Shape}
 */
function buildGoalAreaFill(t, side) {
  const radiusM = 6;
  const goalX = side === "left" ? 0 : 40;
  const innerX = side === "left" ? radiusM : 40 - radiusM;

  return new Konva.Shape({
    listening: false,
    fill: GOAL_AREA_FILL,
    opacity: 0.45,
    sceneFunc: (ctx, shape) => {
      const upper = metersToPx(t, { x: goalX, y: 8.5 });
      const inner1 = metersToPx(t, { x: innerX, y: 8.5 });
      const inner2 = metersToPx(t, { x: innerX, y: 11.5 });
      const lower = metersToPx(t, { x: goalX, y: 11.5 });
      const rPx = metersToPxLength(t, radiusM);

      ctx.beginPath();
      ctx.moveTo(upper.px, upper.py);
      if (side === "left") {
        ctx.arc(upper.px, upper.py, rPx, -Math.PI / 2, 0, false);
      } else {
        ctx.arc(upper.px, upper.py, rPx, Math.PI + Math.PI / 2, Math.PI, true);
      }
      ctx.lineTo(inner2.px, inner2.py);
      if (side === "left") {
        ctx.arc(lower.px, lower.py, rPx, 0, Math.PI / 2, false);
      } else {
        ctx.arc(lower.px, lower.py, rPx, Math.PI, Math.PI / 2, true);
      }
      ctx.lineTo(lower.px, lower.py);
      ctx.closePath();
      ctx.fillStrokeShape(shape);
      void inner1;
    },
  });
}

/**
 * @param {import("konva/lib/Layer.js").Layer} layer
 * @param {import("../core/coords.js").FieldTransform} t
 * @param {"left" | "right"} side
 * @param {number} strokePx
 * @param {number[]} dash
 */
function drawFreeThrowArea(layer, t, side, strokePx, dash) {
  const radiusM = 9;
  const goalX = side === "left" ? 0 : 40;
  const innerX = side === "left" ? radiusM : 40 - radiusM;
  const radiusPx = metersToPxLength(t, radiusM);

  const upperCenter = metersToPx(t, { x: goalX, y: 8.5 });
  layer.add(
    new Konva.Arc({
      x: upperCenter.px,
      y: upperCenter.py,
      innerRadius: radiusPx,
      outerRadius: radiusPx,
      angle: 90,
      rotation: side === "left" ? 270 : 180,
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      dash,
      listening: false,
    }),
  );

  const segTop = metersToPx(t, { x: innerX, y: 8.5 });
  const segBottom = metersToPx(t, { x: innerX, y: 11.5 });
  layer.add(
    new Konva.Line({
      points: [segTop.px, segTop.py, segBottom.px, segBottom.py],
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      dash,
      listening: false,
    }),
  );

  const lowerCenter = metersToPx(t, { x: goalX, y: 11.5 });
  layer.add(
    new Konva.Arc({
      x: lowerCenter.px,
      y: lowerCenter.py,
      innerRadius: radiusPx,
      outerRadius: radiusPx,
      angle: 90,
      rotation: side === "left" ? 0 : 90,
      stroke: LINE_COLOR,
      strokeWidth: strokePx,
      dash,
      listening: false,
    }),
  );
}

/**
 * @param {import("konva/lib/Layer.js").Layer} layer
 * @param {import("../core/coords.js").FieldTransform} t
 * @param {"left" | "right"} side
 * @param {number} strokePx
 */
function draw7mLine(layer, t, side, strokePx) {
  const xStart = side === "left" ? 6.5 : 32.5;
  const xEnd = side === "left" ? 7.5 : 33.5;
  const a = metersToPx(t, { x: xStart, y: 10 });
  const b = metersToPx(t, { x: xEnd, y: 10 });
  layer.add(
    new Konva.Line({
      points: [a.px, a.py, b.px, b.py],
      stroke: LINE_COLOR,
      strokeWidth: strokePx * 1.2,
      listening: false,
    }),
  );
}

/**
 * @param {import("konva/lib/Layer.js").Layer} layer
 * @param {import("../core/coords.js").FieldTransform} t
 * @param {"left" | "right"} side
 * @param {number} strokePx
 */
function draw4mLine(layer, t, side, strokePx) {
  const xStart = side === "left" ? 3.925 : 35.925;
  const xEnd = side === "left" ? 4.075 : 36.075;
  const a = metersToPx(t, { x: xStart, y: 10 });
  const b = metersToPx(t, { x: xEnd, y: 10 });
  layer.add(
    new Konva.Line({
      points: [a.px, a.py, b.px, b.py],
      stroke: LINE_COLOR,
      strokeWidth: strokePx * 1.2,
      listening: false,
    }),
  );
}

/**
 * @param {import("konva/lib/Layer.js").Layer} layer
 * @param {import("../core/coords.js").FieldTransform} t
 * @param {"left" | "right"} side
 * @param {number} goalStrokePx
 */
function drawGoal(layer, t, side, goalStrokePx) {
  const x = side === "left" ? 0 : 40;
  const top = metersToPx(t, { x, y: 8.5 });
  const bottom = metersToPx(t, { x, y: 11.5 });
  layer.add(
    new Konva.Line({
      points: [top.px, top.py, bottom.px, bottom.py],
      stroke: GOAL_COLOR,
      strokeWidth: goalStrokePx,
      lineCap: "square",
      listening: false,
    }),
  );
}
